import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import {
  trackWeatherForecastDayPageViewed,
  trackWeatherForecastDaySelected,
  type WeatherForecastDaySelectHandler,
} from '../helpers/analytics/weatherForecastAnalytics';
import {
  trackExtendedForecastCtaPageViewed,
  trackExtendedForecastDayViewed,
} from '../helpers/analytics/extendedForecastAnalytics';
import {
  EXTENDED_FORECAST_CTA_DAY,
  FREE_FORECAST_DAYS,
  isExtendedForecastCtaDay,
} from '../helpers/forecastHorizon';
import { type WeatherForecastDay } from '../helpers/outlook/formatDays';
import {
  getWeatherForecastDayFromSearch,
  normalizeWeatherForecastDayDate,
  resolveWeatherForecastSelectedDayIndex,
  WEATHER_FORECAST_DAY_QUERY_PARAM,
} from '../helpers/day/dayQuery';

export const useWeatherForecastSelectedDay = (
  forecastDays: WeatherForecastDay[],
  options?: { ctaPageEnabled?: boolean },
) => {
  const history = useHistory();
  const { pathname, search } = useLocation();
  const ctaPageEnabled = Boolean(options?.ctaPageEnabled);
  const lastTrackedPageKeyRef = useRef<string | null>(null);

  const selectedDateFromSearch = getWeatherForecastDayFromSearch(search);
  const defaultDate = forecastDays[0]?.date;
  const ctaPageSelected =
    ctaPageEnabled && isExtendedForecastCtaDay(selectedDateFromSearch);

  const selectedDayIndex = useMemo(
    () =>
      resolveWeatherForecastSelectedDayIndex(
        forecastDays,
        selectedDateFromSearch,
        { ctaPageEnabled },
      ),
    [ctaPageEnabled, forecastDays, selectedDateFromSearch],
  );

  const selectedForecastDay = ctaPageSelected
    ? undefined
    : forecastDays[selectedDayIndex];

  const selectedDate = ctaPageSelected
    ? EXTENDED_FORECAST_CTA_DAY
    : selectedForecastDay?.date;

  const replaceDaySearch = useCallback(
    (nextDayValue: string | undefined) => {
      const params = new URLSearchParams(search);
      if (isExtendedForecastCtaDay(nextDayValue)) {
        params.set(WEATHER_FORECAST_DAY_QUERY_PARAM, EXTENDED_FORECAST_CTA_DAY);
      } else if (
        nextDayValue == null ||
        nextDayValue === normalizeWeatherForecastDayDate(defaultDate)
      ) {
        params.delete(WEATHER_FORECAST_DAY_QUERY_PARAM);
      } else {
        params.set(WEATHER_FORECAST_DAY_QUERY_PARAM, nextDayValue);
      }
      const nextSearch = params.toString();
      history.replace(nextSearch ? `${pathname}?${nextSearch}` : pathname);
    },
    [defaultDate, history, pathname, search],
  );

  const setSelectedDay: WeatherForecastDaySelectHandler = useCallback(
    (date, dayLabel, source = 'day strip') => {
      const normalizedDate = normalizeWeatherForecastDayDate(date);
      const normalizedSelectedDate =
        normalizeWeatherForecastDayDate(selectedDate);

      if (normalizedDate == null || normalizedDate === normalizedSelectedDate) {
        return;
      }

      const normalizedDefaultDate =
        normalizeWeatherForecastDayDate(defaultDate);
      replaceDaySearch(
        normalizedDate === normalizedDefaultDate ? undefined : normalizedDate,
      );

      trackWeatherForecastDaySelected(normalizedDate, dayLabel, source);
    },
    [defaultDate, replaceDaySearch, selectedDate],
  );

  const selectCtaPage = useCallback(() => {
    if (!ctaPageEnabled || isExtendedForecastCtaDay(selectedDate)) {
      return;
    }

    replaceDaySearch(EXTENDED_FORECAST_CTA_DAY);
  }, [ctaPageEnabled, replaceDaySearch, selectedDate]);

  // Pageview for the settled selected day (deep links, default today, swipe,
  // and strip taps). Deduped so re-renders of the same page do not re-fire.
  useEffect(() => {
    if (!forecastDays.length || selectedDate == null) {
      return;
    }

    if (ctaPageSelected) {
      const pageKey = EXTENDED_FORECAST_CTA_DAY;
      if (lastTrackedPageKeyRef.current === pageKey) {
        return;
      }
      lastTrackedPageKeyRef.current = pageKey;
      trackExtendedForecastCtaPageViewed();
      return;
    }

    if (!selectedForecastDay) {
      return;
    }

    const normalizedDate = normalizeWeatherForecastDayDate(
      selectedForecastDay.date,
    );
    if (normalizedDate == null) {
      return;
    }

    const pageKey = normalizedDate;
    if (lastTrackedPageKeyRef.current === pageKey) {
      return;
    }
    lastTrackedPageKeyRef.current = pageKey;

    trackWeatherForecastDayPageViewed({
      date: normalizedDate,
      dayLabel: selectedForecastDay.dayLabel,
      dayIndex: selectedDayIndex,
    });

    if (selectedDayIndex >= FREE_FORECAST_DAYS) {
      trackExtendedForecastDayViewed({
        date: normalizedDate,
        dayLabel: selectedForecastDay.dayLabel,
      });
    }
  }, [
    ctaPageSelected,
    forecastDays.length,
    selectedDate,
    selectedDayIndex,
    selectedForecastDay,
  ]);

  return {
    selectedDayIndex,
    selectedDate,
    selectedForecastDay,
    defaultDate,
    setSelectedDay,
    selectCtaPage,
  };
};
